import { useCallback, useEffect, useRef } from 'react';
import { CONFIG } from 'src/config-global';
import { CreateLoginHistoryPayload, loginHistoryApi } from 'src/features/services/loginHistory';

type Options = {
  /**
   * Nếu true, khi đóng tab/ẩn tab/mất mạng sẽ gọi close phiên theo userId
   * (không cần biết loginHistoryId). Phù hợp khi bạn KHÔNG lưu id record.
   * Nếu false, sẽ ưu tiên đóng theo loginHistoryId (chính xác hơn nếu có).
   */
  closeByUserOnly?: boolean;
};

export function useLoginHistory(userId?: string, opts: Options = {}) {
  const loginHistoryIdRef = useRef<string | null>(null);

  const openSession = useCallback(
    async (extra?: Omit<CreateLoginHistoryPayload, 'userId'>) => {
      if (!userId) return null;
      try {
        const payload: CreateLoginHistoryPayload = {
          userId,
          deviceType: detectDeviceType(), // helper bên dưới
          browserName: getBrowserName(),
          browserVersion: getBrowserVersion(),
          osName: getOsName(),
          osVersion: '',
          cpuArchitecture: '',
          ...extra,
        };
        const rec = await loginHistoryApi.create(payload);
        loginHistoryIdRef.current = rec.id;
        return rec;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[loginHistory] openSession error', e);
        return null;
      }
    },
    [userId]
  );

  const sendBeaconJson = (url: string, body: any) => {
    try {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      return navigator.sendBeacon(url, blob);
    } catch {
      return false;
    }
  };

  const closeSessionFast = useCallback(async () => {
    if (!userId) return;

    const id = loginHistoryIdRef.current;
    if (id && !opts.closeByUserOnly) {
      const url = `${CONFIG.site.serverUrl}/login-history/logout`;
      const ok = sendBeaconJson(url, {});
      if (!ok) {
        // fallback axios
        try {
          await loginHistoryApi.logoutById();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[loginHistory] logoutById fallback error', e);
        }
      }
      return;
    }

    // 2) Nếu không có id hoặc bạn ép chỉ đóng theo user
    const url2 = `${CONFIG.site.serverUrl}/login-history/close-latest/by-user`;
    const ok2 = sendBeaconJson(url2, { userId });
    if (!ok2) {
      try {
        await loginHistoryApi.closeLatestByUser(userId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[loginHistory] closeLatestByUser fallback error', e);
      }
    }
  }, [userId, opts.closeByUserOnly]);

  // Đăng ký các guard: beforeunload, visibilitychange, offline
  useEffect(() => {
    if (!userId) return;

    const onBeforeUnload = () => {
      closeSessionFast();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        closeSessionFast();
      }
    };

    const onOffline = () => {
      closeSessionFast();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('offline', onOffline);
    };
  }, [userId, closeSessionFast]);

  return {
    openSession,
    closeSession: closeSessionFast,
    get loginHistoryId() {
      return loginHistoryIdRef.current;
    },
  };
}

/** ============== Helpers đơn giản, bạn có thể thay bằng UA parser nếu muốn ============== */

function detectDeviceType(): 'desktop' | 'mobile' | 'tablet' | 'bot' {
  const ua = navigator.userAgent.toLowerCase();
  if (/bot|crawler|spider|crawling/i.test(ua)) return 'bot';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  if (/mobi|android/i.test(ua)) return 'mobile';
  return 'desktop';
}
function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  return 'Unknown';
}
function getBrowserVersion(): string {
  const m = navigator.userAgent.match(/(Chrome|Firefox|Edg|Version)\/([\d.]+)/);
  return m?.[2] ?? '';
}
function getOsName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  return 'Unknown';
}
